/**
 * Sidebar: Automations → Chatbot AI (`chatbotai`).
 * Manual builder + AI generate. Lists conversation and chatbot_ai flows.
 */
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCheckCircle,
  FaEdit,
  FaFileUpload,
  FaPencilAlt,
  FaPlus,
  FaPowerOff,
  FaProjectDiagram,
  FaSearch,
} from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import {
  generateChatbotAiFlowFromDoc,
  getChatbotAiFlowGraph,
  getConversationFlowGraph,
  listChatbotAiFlows,
  listConversationFlows,
  saveChatbotAiFlowGraph,
  saveConversationFlowGraph,
  setChatbotAiFlowActive,
  setConversationFlowActive,
} from "../../api/chatbotAi.api";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { validatePublishGraph } from "../../utils/conversationFlowGraph";
import StatusCapsule from "../StatusCapsule";

const WhatsAppFlowCanvas = lazy(() => import("./WhatsAppFlowCanvas"));

const emptyForm = () => ({
  id: "",
  name: "",
  triggers: [],
  status: "draft",
  isActive: false,
  kind: "chatbot_ai",
});

const flowKindOf = (flow) => {
  const kind = String(flow?.kind || "").toLowerCase();
  return kind === "conversation" ? "conversation" : "chatbot_ai";
};

const isConversationFlow = (flow) => flowKindOf(flow) === "conversation";

const mergeFlowLists = (...lists) => {
  const seen = new Set();
  const merged = [];
  lists.flat().forEach((flow) => {
    if (!flow) return;
    const id = String(flow.id || "");
    if (!id || seen.has(id)) return;
    seen.add(id);
    merged.push({ ...flow, kind: flowKindOf(flow) });
  });
  return merged;
};

const ACCEPT_DOCS =
  ".pdf,.docx,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const emptyDocForm = () => ({
  file: null,
  prompt: "",
  flowName: "",
  target: "page",
});

const normalizeTriggerValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const parseFlowTriggers = (flow) => {
  if (Array.isArray(flow?.triggers) && flow.triggers.length) {
    return flow.triggers.map(normalizeTriggerValue).filter(Boolean);
  }
  if (Array.isArray(flow?.triggerJson) && flow.triggerJson.length) {
    return flow.triggerJson.map(normalizeTriggerValue).filter(Boolean);
  }
  const raw = flow?.trigger;
  if (Array.isArray(raw)) {
    return raw.map(normalizeTriggerValue).filter(Boolean);
  }
  const text = String(raw || "").trim();
  if (!text) return [];
  return text
    .split(/[,|\n]/)
    .map(normalizeTriggerValue)
    .filter(Boolean);
};

const ChatbotAI = () => {
  const { projectId, phoneNoId, projectLoading, needsWhatsAppSetup } =
    useProjectPhone();
  const [flows, setFlows] = useState([]);
  const [search, setSearch] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [graph, setGraph] = useState({ stages: [], transitions: [] });
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [canvasEpoch, setCanvasEpoch] = useState(0);
  const [triggerDraft, setTriggerDraft] = useState("");
  const [statusBusyId, setStatusBusyId] = useState("");
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [docForm, setDocForm] = useState(emptyDocForm);
  const [docBusy, setDocBusy] = useState(false);
  const [docError, setDocError] = useState("");
  const canvasRef = useRef(null);
  const docInputRef = useRef(null);

  const loadFlows = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const [aiRes, convRes] = await Promise.all([
        listChatbotAiFlows({ projectId }).catch(() => ({ flows: [] })),
        listConversationFlows({ projectId }).catch(() => ({ flows: [] })),
      ]);
      setFlows(mergeFlowLists(aiRes?.flows || [], convRes?.flows || []));
    } catch (err) {
      setError(err.message || "Failed to load Chatbot AI flows");
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  useEffect(() => {
    if (!modalOpen || !phoneNoId || !projectId) {
      if (!modalOpen) setTemplates([]);
      return undefined;
    }
    let cancelled = false;
    apihubGet(
      `/getInteractedCustomerDetail/gettotaltemplatesbyphonenumberid/${phoneNoId}?page=1&limit=20&sortBy=createdAt&sortOrder=desc&projectId=${encodeURIComponent(
        projectId,
      )}`,
    )
      .then((res) => {
        if (cancelled) return;
        const list = res?.data || [];
        setTemplates(
          list.filter(
            (tpl) =>
              String(tpl.template_status || tpl.status || "").toUpperCase() ===
              "APPROVED",
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, phoneNoId, projectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter((flow) => {
      const name = String(flow.name || "").toLowerCase();
      const id = String(flow.id || "").toLowerCase();
      const trigger = (flow.triggers || parseFlowTriggers(flow)).join(" ");
      return name.includes(q) || id.includes(q) || trigger.includes(q);
    });
  }, [flows, search]);

  const stats = useMemo(
    () => ({
      total: flows.length,
      active: flows.filter((flow) => flow.isActive || flow.status === "active")
        .length,
      draft: flows.filter(
        (flow) => String(flow.status || "").toLowerCase() === "draft",
      ).length,
    }),
    [flows],
  );

  const deactivateFlow = async (flow) => {
    if (!projectId || !flow?.id) return;
    setStatusBusyId(String(flow.id));
    setError("");
    setMessage("");
    try {
      const toggleActive = isConversationFlow(flow)
        ? setConversationFlowActive
        : setChatbotAiFlowActive;
      await toggleActive(
        { flowId: flow.id },
        { projectId, flowId: flow.id, isActive: false },
      );
      setMessage(`"${flow.name || "Flow"}" deactivated.`);
      await loadFlows();
    } catch (err) {
      setError(err.message || "Failed to deactivate flow");
    } finally {
      setStatusBusyId("");
    }
  };

  const closeEditor = () => {
    setModalOpen(false);
    setError("");
    setForm(emptyForm());
    setTriggerDraft("");
    setGraph({ stages: [], transitions: [] });
  };

  const closeDocModal = () => {
    setDocModalOpen(false);
    setDocForm(emptyDocForm());
    setDocError("");
    setDocBusy(false);
    if (docInputRef.current) docInputRef.current.value = "";
  };

  const openDocModal = (target = "page") => {
    if (!projectId) return;
    setMessage("");
    setError("");
    setDocError("");
    setDocForm({
      ...emptyDocForm(),
      target,
      flowName: target === "editor" ? form.name : "",
    });
    setDocModalOpen(true);
  };

  const applyGeneratedFlow = (res, replaceEditor) => {
    const stages = Array.isArray(res?.stages) ? res.stages : [];
    const transitions = Array.isArray(res?.transitions) ? res.transitions : [];
    const suggestedName = String(res?.flowName || "").trim();
    const suggestedTriggers = Array.isArray(res?.triggers)
      ? res.triggers.map(normalizeTriggerValue).filter(Boolean)
      : [];
    if (replaceEditor) {
      setForm((prev) => ({
        ...prev,
        name: prev.name || suggestedName || prev.name,
        triggers: prev.triggers.length ? prev.triggers : suggestedTriggers,
      }));
    } else {
      setForm({
        id: "",
        name: suggestedName,
        triggers: suggestedTriggers,
        status: "draft",
        isActive: false,
        kind: "chatbot_ai",
      });
      setTriggerDraft("");
      setModalOpen(true);
    }
    setGraph({ stages, transitions });
    setCanvasEpoch((n) => n + 1);
    const model = res?.source?.model;
    const base = res?.usedAi
      ? `AI${model ? ` (${model})` : ""} generated the flow and loaded it on the canvas.`
      : "Flow generated from the document and loaded onto the canvas.";
    setMessage(res?.warning ? `${base} ${res.warning}` : base);
  };

  const runGenerateFromDoc = async () => {
    if (!projectId) return;
    if (!docForm.file && !String(docForm.prompt || "").trim()) {
      setDocError("Upload a PDF, DOCX, or XLSX file, or enter an AI prompt.");
      return;
    }
    setDocBusy(true);
    setDocError("");
    try {
      const payload = new FormData();
      payload.append("projectId", projectId);
      if (docForm.file) payload.append("file", docForm.file);
      if (String(docForm.prompt || "").trim()) {
        payload.append("prompt", String(docForm.prompt).trim());
      }
      if (String(docForm.flowName || "").trim()) {
        payload.append("flowName", String(docForm.flowName).trim());
      }
      const res = await generateChatbotAiFlowFromDoc(payload);
      const replaceEditor = docForm.target === "editor" && modalOpen;
      applyGeneratedFlow(res, replaceEditor);
      closeDocModal();
    } catch (err) {
      setDocError(err.message || "Failed to generate flow from document");
    } finally {
      setDocBusy(false);
    }
  };

  const onDocFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    setDocForm((prev) => ({ ...prev, file: file || null }));
    setDocError("");
  };

  const onDocDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer?.files && event.dataTransfer.files[0];
    if (!file) return;
    setDocForm((prev) => ({ ...prev, file }));
    setDocError("");
  };

  const openCreate = () => {
    if (!projectId) return;
    setMessage("");
    setError("");
    setForm({
      id: "",
      name: "",
      triggers: [],
      status: "draft",
      isActive: false,
      kind: "chatbot_ai",
    });
    setTriggerDraft("");
    setGraph({ stages: [], transitions: [] });
    setCanvasEpoch((n) => n + 1);
    setModalOpen(true);
  };

  const openEdit = (flow) => {
    setMessage("");
    setError("");
    setForm({
      id: flow.id,
      name: flow.name || "",
      triggers: parseFlowTriggers(flow),
      status: flow.status || "draft",
      isActive: Boolean(flow.isActive),
      kind: flowKindOf(flow),
    });
    setTriggerDraft("");
    setGraph({ stages: [], transitions: [] });
    setCanvasEpoch((n) => n + 1);
    setModalOpen(true);
    if (!projectId || !flow.id) return;
    const flowId = flow.id;
    const loadGraph = isConversationFlow(flow)
      ? getConversationFlowGraph
      : getChatbotAiFlowGraph;
    loadGraph({ flowId }, { projectId })
      .then((res) => {
        setForm((prev) =>
          prev.id !== flowId
            ? prev
            : {
                id: res?.flow?.id || flowId,
                name: res?.flow?.name || prev.name || "",
                triggers: parseFlowTriggers(res?.flow || prev),
                status: res?.flow?.status || prev.status || "draft",
                isActive: Boolean(res?.flow?.isActive ?? prev.isActive),
                kind: flowKindOf(res?.flow || prev),
              },
        );
        setGraph({
          stages: res?.stages || [],
          transitions: res?.transitions || [],
        });
        setCanvasEpoch((n) => n + 1);
      })
      .catch((err) => {
        if (/not found/i.test(String(err.message || ""))) {
          setGraph({ stages: [], transitions: [] });
          return;
        }
        setError(err.message || "Failed to load Chatbot AI graph");
      });
  };

  const persistGraph = async ({ publish, keepOpen = false }) => {
    const name = String(form.name || "").trim();
    if (!name) {
      setError("Flow name is required");
      return;
    }
    if (!projectId) return;
    const pending = normalizeTriggerValue(triggerDraft);
    const triggers = [...form.triggers];
    if (pending && !triggers.includes(pending)) triggers.push(pending);
    const payload = canvasRef.current?.getGraph?.() || {
      stages: [],
      transitions: [],
    };
    if (publish) {
      const problem = validatePublishGraph(payload);
      if (problem) {
        setError(problem);
        return;
      }
      if (!triggers.length) {
        const clash = flows.find((row) => {
          if (form.id && String(row.id) === String(form.id)) return false;
          if (!(row.isActive || String(row.status || "").toLowerCase() === "active")) {
            return false;
          }
          return parseFlowTriggers(row).length === 0;
        });
        if (clash) {
          setError(
            `Only one published Chatbot AI flow can have a blank trigger. "${clash.name || clash.id}" is already active without a trigger.`,
          );
          return;
        }
      } else {
        const wanted = new Set(triggers);
        const clash = flows.find((row) => {
          if (form.id && String(row.id) === String(form.id)) return false;
          if (!(row.isActive || String(row.status || "").toLowerCase() === "active")) {
            return false;
          }
          return parseFlowTriggers(row).some((item) => wanted.has(item));
        });
        if (clash) {
          const hit = parseFlowTriggers(clash).find((item) => wanted.has(item));
          setError(
            `Trigger "${hit}" is already used by active Chatbot AI flow "${clash.name || clash.id}".`,
          );
          return;
        }
      }
    }
    setSaving(true);
    setError("");
    try {
      const pathId = form.id || "new";
      const saveGraphApi =
        form.id && form.kind === "conversation"
          ? saveConversationFlowGraph
          : saveChatbotAiFlowGraph;
      const saved = await saveGraphApi(
        { flowId: pathId },
        {
          projectId,
          ...(form.id ? { flowId: form.id } : {}),
          flowName: name,
          triggers,
          triggerJson: triggers,
          isActive: Boolean(publish),
          stages: payload.stages,
          transitions: payload.transitions,
        },
      );
      if (saved?.flow?.id) {
        setForm((prev) => ({ ...prev, id: saved.flow.id }));
      }
      setMessage(publish ? "Chatbot AI flow published." : "Chatbot AI flow saved as draft.");
      if (!keepOpen) {
        closeEditor();
      }
      loadFlows();
      return saved;
    } catch (err) {
      setError(err.message || "Failed to save Chatbot AI flow");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openLinkedFlow = async (flowId) => {
    const targetId = String(flowId || "").trim();
    if (!targetId) return;
    if (form.name) {
      await persistGraph({ publish: false, keepOpen: true });
    }
    const target = flows.find((row) => String(row.id) === targetId);
    openEdit(target || { id: targetId, name: "", triggers: [] });
  };

  const addTriggers = (raw) => {
    const next = String(raw || "")
      .split(/[,|\n]/)
      .map(normalizeTriggerValue)
      .filter(Boolean);
    if (!next.length) return;
    setForm((prev) => {
      const seen = new Set(prev.triggers);
      const merged = [...prev.triggers];
      next.forEach((item) => {
        if (seen.has(item)) return;
        seen.add(item);
        merged.push(item);
      });
      return { ...prev, triggers: merged };
    });
    setTriggerDraft("");
  };

  const removeTrigger = (value) => {
    setForm((prev) => ({
      ...prev,
      triggers: prev.triggers.filter((item) => item !== value),
    }));
  };

  const onTriggerKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addTriggers(triggerDraft);
    }
    if (event.key === "Backspace" && !triggerDraft && form.triggers.length) {
      removeTrigger(form.triggers[form.triggers.length - 1]);
    }
  };

  const saveGraph = async (event) => {
    if (event) event.preventDefault();
    await persistGraph({ publish: false });
  };

  const publishGraph = async (event) => {
    if (event) event.preventDefault();
    await persistGraph({ publish: true });
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-flows-header">
        <div>
          <h1>
            <FaProjectDiagram />
            Chatbot AI
          </h1>
          <p className="biz-panel-subtitle">
            Build with Message, Button, Radio, Dropdown, Media, Input, API,
            Condition and End stages — or describe the bot to AI / attach a
            PDF, DOCX, or XLSX. Generated Flow JSON opens on the canvas.
          </p>
        </div>
        <div className="biz-flows-header-actions">
          <button
            type="button"
            className="biz-btn-secondary"
            onClick={() => openDocModal("page")}
            disabled={projectLoading || needsWhatsAppSetup}
          >
            <FaFileUpload />
            Generate with AI
          </button>
          <button
            type="button"
            className="biz-btn-primary"
            onClick={openCreate}
            disabled={projectLoading || needsWhatsAppSetup}
          >
            <FaPlus />
            Create Flow
          </button>
        </div>
      </div>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}
      {needsWhatsAppSetup && (
        <p className="biz-banner biz-banner-error">
          Connect WhatsApp for this project before creating a Chatbot AI flow.
        </p>
      )}

      <div className="biz-flows-stats">
        <div className="biz-flows-stat-card biz-flows-stat-total">
          <div className="biz-flows-stat-icon">
            <FaProjectDiagram />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Flows</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="biz-flows-stat-card biz-flows-stat-active">
          <div className="biz-flows-stat-icon">
            <FaCheckCircle />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Active</span>
            <strong>{stats.active}</strong>
          </div>
        </div>
        <div className="biz-flows-stat-card biz-flows-stat-draft">
          <div className="biz-flows-stat-icon">
            <FaPencilAlt />
          </div>
          <div className="biz-flows-stat-copy">
            <span>Draft</span>
            <strong>{stats.draft}</strong>
          </div>
        </div>
      </div>

      <div className="biz-table-panel">
        <div className="biz-table-toolbar">
          <div className="biz-panel-search biz-table-search">
            <FaSearch className="biz-panel-search-icon" />
            <input
              type="search"
              placeholder="Search by flow name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="biz-table-wrap">
        <table className="biz-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Flow ID</th>
              <th>Flow Name</th>
              <th>Trigger</th>
              <th>Status</th>
              <th className="biz-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="biz-table-empty">
                  {loading
                    ? "Loading flows…"
                    : "No flows yet. Create a flow or generate one with AI."}
                </td>
              </tr>
            ) : (
              filtered.map((flow, idx) => (
                <tr key={flow.id || idx}>
                  <td>{idx + 1}</td>
                  <td className="biz-table-mono">{flow.id || "—"}</td>
                  <td>{flow.name || "Unnamed Flow"}</td>
                  <td>
                    {(flow.triggers && flow.triggers.length
                      ? flow.triggers
                      : parseFlowTriggers(flow)
                    ).join(", ") || "—"}
                  </td>
                  <td>
                    <StatusCapsule
                      value={flow.isActive ? "Active" : flow.status || "Draft"}
                    />
                  </td>
                  <td className="biz-col-actions">
                    <div className="biz-table-actions biz-table-actions-fixed">
                      <button
                        type="button"
                        className="biz-btn-icon"
                        title="Edit"
                        onClick={() => openEdit(flow)}
                      >
                        <FaEdit />
                      </button>
                      {Boolean(flow.isActive) ? (
                        <button
                          type="button"
                          className="biz-btn-icon biz-btn-icon-danger"
                          title="Deactivate"
                          disabled={statusBusyId === String(flow.id)}
                          onClick={() => deactivateFlow(flow)}
                        >
                          <FaPowerOff />
                        </button>
                      ) : (
                        <span className="biz-btn-icon-spacer" aria-hidden="true" />
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {modalOpen && (
        <div className="wa-flow-builder-overlay" role="dialog" aria-modal="true">
          <div className="wa-conv-flow-shell">
            <header className="wa-flow-builder-top">
              <div>
                <h2>Chatbot AI Builder</h2>
                <p>
                  Drag nodes onto the canvas. Connect Button, API, and Condition
                  handles to the next stage.
                </p>
              </div>
              <div className="wa-flow-builder-top-actions">
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={() => openDocModal("editor")}
                  disabled={saving || docBusy}
                >
                  <FaFileUpload />
                  AI prompt
                </button>
                <button
                  type="button"
                  className="biz-btn-secondary"
                  onClick={closeEditor}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="biz-btn-secondary"
                  disabled={saving}
                  onClick={saveGraph}
                >
                  {saving ? "Please wait…" : "Save"}
                </button>
                <button
                  type="button"
                  className="biz-btn-primary"
                  disabled={saving}
                  onClick={publishGraph}
                >
                  {saving ? "Please wait…" : "Publish"}
                </button>
              </div>
            </header>
            <div className="wa-conv-flow-body">
              {error && <p className="biz-banner biz-banner-error">{error}</p>}
              <div className="wa-conv-flow-meta">
                <label className="wa-conv-flow-field">
                  <span>Flow name</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => {
                      setForm((prev) => ({ ...prev, name: event.target.value }));
                      if (error === "Flow name is required") setError("");
                    }}
                    placeholder="Enter flow name"
                    required
                    autoFocus
                  />
                </label>
                <div className="wa-conv-flow-field wa-conv-flow-triggers">
                  <span>Triggers</span>
                  <div className="wa-conv-flow-trigger-box">
                    {form.triggers.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className="wa-conv-flow-chip"
                        onClick={() => removeTrigger(item)}
                        title={`Remove ${item}`}
                      >
                        {item}
                        <span aria-hidden>×</span>
                      </button>
                    ))}
                    <input
                      type="text"
                      value={triggerDraft}
                      onChange={(event) => setTriggerDraft(event.target.value)}
                      onKeyDown={onTriggerKeyDown}
                      onBlur={() => {
                        if (triggerDraft.trim()) addTriggers(triggerDraft);
                      }}
                      placeholder={
                        form.triggers.length
                          ? "Add another trigger"
                          : "e.g. order"
                      }
                      maxLength={64}
                    />
                    <button
                      type="button"
                      className="biz-btn-secondary wa-conv-flow-trigger-add"
                      onClick={() => addTriggers(triggerDraft)}
                      disabled={!triggerDraft.trim()}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
              <Suspense
                fallback={
                  <div className="wa-conv-flow-builder">Loading editor…</div>
                }
              >
                <WhatsAppFlowCanvas
                  key={`${form.id || "new"}-${canvasEpoch}`}
                  ref={canvasRef}
                  initialStages={graph.stages}
                  initialTransitions={graph.transitions}
                  templates={templates}
                  conversationFlows={flows}
                  currentFlowId={form.id}
                  onOpenLinkedFlow={openLinkedFlow}
                />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {docModalOpen && (
        <div
          className="wa-ai-doc-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="wa-ai-doc-title"
        >
          <div className="wa-ai-doc-dialog">
            <header className="wa-ai-doc-header">
              <div>
                <h2 id="wa-ai-doc-title">Generate with AI</h2>
                <p>
                  Describe the chatbot in the prompt the way you would ask
                  AI. You can also attach a PDF, Word, or Excel file as
                  extra context.
                </p>
              </div>
              <button
                type="button"
                className="biz-btn-secondary"
                onClick={closeDocModal}
                disabled={docBusy}
              >
                Close
              </button>
            </header>
            {docError && <p className="biz-banner biz-banner-error">{docError}</p>}
            <label
              className={`wa-ai-doc-drop${docForm.file ? " is-filled" : ""}`}
              onDragOver={(event) => event.preventDefault()}
              onDrop={onDocDrop}
            >
              <FaFileUpload />
              <strong>
                {docForm.file
                  ? docForm.file.name
                  : "Drop PDF, DOCX, or XLSX here"}
              </strong>
              <span>or click to browse. Max 10 MB.</span>
              <input
                ref={docInputRef}
                type="file"
                accept={ACCEPT_DOCS}
                onChange={onDocFileChange}
                hidden
              />
            </label>
            <label className="wa-conv-flow-field wa-ai-doc-field">
              <span>Flow name (optional)</span>
              <input
                type="text"
                value={docForm.flowName}
                onChange={(event) =>
                  setDocForm((prev) => ({
                    ...prev,
                    flowName: event.target.value,
                  }))
                }
                placeholder="e.g. Product FAQ bot"
                maxLength={80}
              />
            </label>
            <label className="wa-conv-flow-field wa-ai-doc-field">
              <span>AI prompt</span>
              <textarea
                value={docForm.prompt}
                onChange={(event) =>
                  setDocForm((prev) => ({ ...prev, prompt: event.target.value }))
                }
                placeholder="e.g. Design a WhatsApp chatbot for a pizza shop. Start with a menu for order, hours and support. Ask for the pizza name, then close politely."
                rows={6}
              />
            </label>
            <div className="wa-ai-doc-actions">
              <button
                type="button"
                className="biz-btn-secondary"
                onClick={closeDocModal}
                disabled={docBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="biz-btn-primary"
                onClick={runGenerateFromDoc}
                disabled={docBusy || projectLoading}
              >
                {docBusy ? "Generating with AI…" : "Generate with AI"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotAI;
